<template>
  <div class="alert-history p-3">
    <div class="d-flex flex-wrap mb-2" style="gap: 10px;">
      <div>
        <span class="d-block text-muted small font-weight-bold">Asset</span>
        <b-form-select v-model="filters.massId" style="min-width: 200px;">
          <template v-slot:first>
            <option value="">All Assets</option>
          </template>
          <option v-for="a in assetOptions" :key="a.value" :value="a.value">{{ a.text }}</option>
        </b-form-select>
      </div>

      <div>
        <span class="d-block text-muted small font-weight-bold">Status</span>
        <b-form-select v-model="filters.status" style="min-width: 150px;">
          <template v-slot:first>
            <option value="">All Status</option>
          </template>
          <option value="ACTIVE">Active</option>
          <option value="RESOLVED">Resolved</option>
        </b-form-select>
      </div>

      <div>
        <span class="d-block text-muted small font-weight-bold">Rule</span>
        <b-form-select v-model="filters.alruId" style="min-width: 220px;">
          <template v-slot:first>
            <option value="">All Rules</option>
          </template>
          <option v-for="r in ruleOptions" :key="r.value" :value="r.value">{{ r.text }}</option>
        </b-form-select>
      </div>

      <div>
        <span class="d-block text-muted small font-weight-bold">Severity</span>
        <b-form-select v-model="filters.severity" style="min-width: 150px;">
          <template v-slot:first>
            <option value="">All Severity</option>
          </template>
          <option value="CRITICAL">Critical</option>
          <option value="WARNING">Warning</option>
        </b-form-select>
      </div>

      <div>
        <span class="d-block text-muted small font-weight-bold">Date Range</span>
        <v-date-picker v-model="filters.dateRange" is-range mode="date" :masks="{ input: 'DD-MM-YYYY' }">
          <template #default="{ inputValue, inputEvents }">
            <input
              class="form-control bg-light border rounded"
              style="min-width: 220px;"
              readonly
              :value="(inputValue.start || 'Start') + ' - ' + (inputValue.end || 'End')"
              v-on="inputEvents.start" />
          </template>
        </v-date-picker>
      </div>
    </div>

    <div v-if="rawRows.length" class="text-muted small mb-2">
      Showing the {{ rawRows.length }} most recent incidents matching your Asset/Status/Rule filters
      (oldest loaded: {{ formatDate(oldestLoadedAt) }}). Narrow those filters to see further back —
      Severity and Date Range only search within what's currently loaded.
    </div>

    <div v-if="isLoading" class="text-center my-4">
      <b-spinner variant="success" small class="align-middle"></b-spinner>
    </div>

    <div v-else-if="loadError" class="text-center text-danger my-4">
      <strong>{{ errorMessage || 'Failed to load alert history.' }}</strong>
      <div>
        <button class="btn btn-sm btn-outline-primary mt-2" @click="fetchIncidents">Retry</button>
      </div>
    </div>

    <div v-else-if="!pagedRows.length" class="text-center text-muted my-4">
      <strong>No incidents found.</strong>
    </div>

    <div v-else>
      <b-table striped hover fixed head-variant="dark" small responsive :fields="fields" :items="pagedRows">
        <template v-slot:cell(status)="data">
          <b-badge :variant="data.value === 'ACTIVE' ? 'danger' : 'secondary'">
            {{ data.value === 'ACTIVE' ? 'Active' : 'Resolved' }}
          </b-badge>
        </template>
        <template v-slot:cell(severity)="data">
          <b-badge :variant="data.value === 'CRITICAL' ? 'danger' : 'warning'">{{ data.value }}</b-badge>
        </template>
      </b-table>

      <b-pagination
        v-model="pagination.current"
        :per-page="pagination.pageSize"
        :total-rows="filteredRows.length">
      </b-pagination>
    </div>
  </div>
</template>

<script>
import AlertIncidentSvc from '@/services/AlertIncidentSvc';
import getScopedAssets from '@/util/scopedAssets';
import moment from 'moment';

const HISTORY_BATCH_SIZE = 500;

function severityOf(incident) {
  return incident?.alinAlru?.alruSeverity === 'CRITICAL' ? 'CRITICAL' : 'WARNING';
}

function buildRows(incidents) {
  return incidents.map((i) => ({
    id: i.alinId,
    massId: i.alinMassId,
    shipName: i.alinMass?.massName || '',
    ruleId: i.alinAlruId,
    ruleName: i.alinAlru?.alruName || 'Alert',
    severity: severityOf(i),
    status: i.alinStatus,
    startedAt: i.alinStartedAt,
    resolvedAt: i.alinResolvedAt,
    duration: i.alinResolvedAt
      ? moment(i.alinResolvedAt).diff(moment(i.alinStartedAt), 'seconds')
      : null,
  }));
}

export default {
  name: 'AlertHistory',
  data() {
    return {
      rawRows: [],
      assetOptions: [],
      isLoading: false,
      loadError: false,
      errorMessage: '',
      fetchSeq: 0,
      filters: {
        massId: '',
        status: '',
        alruId: '',
        severity: '',
        dateRange: { start: null, end: null },
      },
      pagination: {
        current: 1,
        pageSize: 25,
      },
      fields: [
        {
          key: 'startedAt',
          label: 'Started At',
          formatter: (v) => (v ? moment.utc(v).local().format('YYYY-MM-DD HH:mm:ss Z') : '-'),
        },
        {
          key: 'resolvedAt',
          label: 'Resolved At',
          formatter: (v) => (v ? moment.utc(v).local().format('YYYY-MM-DD HH:mm:ss Z') : '-'),
        },
        { key: 'shipName', label: 'Asset' },
        { key: 'ruleName', label: 'Rule' },
        { key: 'severity', label: 'Severity' },
        { key: 'status', label: 'Status' },
        {
          key: 'duration',
          label: 'Duration',
          formatter: (v) => {
            if (v === null || v === undefined) return '-';
            const pad = (n) => String(n).padStart(2, '0');
            const h = Math.floor(v / 3600);
            const m = Math.floor((v % 3600) / 60);
            const s = v % 60;
            return `${pad(h)}:${pad(m)}:${pad(s)}`;
          },
        },
      ],
    };
  },
  computed: {
    serverFilterKey() {
      return `${this.filters.massId}|${this.filters.status}|${this.filters.alruId}`;
    },
    ruleOptions() {
      const seen = new Map();
      this.rawRows.forEach((r) => {
        if (r.ruleId && !seen.has(r.ruleId)) {
          seen.set(r.ruleId, r.ruleName);
        }
      });
      return Array.from(seen.entries()).map(([value, text]) => ({ value, text }));
    },
    oldestLoadedAt() {
      if (!this.rawRows.length) return null;
      // rawRows is server-sorted alinStartedAt DESC, so the last row is the oldest.
      return this.rawRows[this.rawRows.length - 1].startedAt;
    },
    filteredRows() {
      return this.rawRows.filter((r) => {
        if (this.filters.severity && r.severity !== this.filters.severity) return false;
        if (this.filters.dateRange?.start
          && moment.utc(r.startedAt).local().isBefore(moment(this.filters.dateRange.start).startOf('day'))) {
          return false;
        }
        if (this.filters.dateRange?.end
          && moment.utc(r.startedAt).local().isAfter(moment(this.filters.dateRange.end).endOf('day'))) {
          return false;
        }
        return true;
      });
    },
    pagedRows() {
      const start = (this.pagination.current - 1) * this.pagination.pageSize;
      return this.filteredRows.slice(start, start + this.pagination.pageSize);
    },
  },
  watch: {
    'filters.massId': function onMassIdChange() {
      this.filters.alruId = '';
    },
    'filters.status': function onStatusChange() {
      this.filters.alruId = '';
    },
    serverFilterKey() {
      this.pagination.current = 1;
      this.fetchIncidents();
    },
    'filters.severity': function onSeverityChange() {
      this.pagination.current = 1;
    },
    'filters.dateRange': {
      deep: true,
      handler() {
        this.pagination.current = 1;
      },
    },
  },
  mounted() {
    this.fetchIncidents();
  },
  methods: {
    formatDate(v) {
      if (!v) return '-';
      return moment.utc(v).local().format('YYYY-MM-DD HH:mm:ss Z');
    },
    async fetchIncidents() {
      this.fetchSeq = (this.fetchSeq || 0) + 1;
      const seq = this.fetchSeq;
      this.isLoading = true;
      this.loadError = false;
      this.errorMessage = '';
      try {
        let assets = [];
        try {
          assets = await getScopedAssets();
        } catch (e) {
          console.error('alert history: failed to load scoped assets:', e);
          if (seq === this.fetchSeq) {
            this.loadError = true;
            this.errorMessage = "Failed to load your company's assets.";
            this.rawRows = [];
          }
          return;
        }

        if (seq !== this.fetchSeq) return;
        this.assetOptions = assets.map((a) => ({ value: a.massId, text: a.massName }));
        const massIds = assets.map((a) => a.massId).filter(Boolean);

        if (!massIds.length) {
          this.rawRows = [];
          return;
        }

        const finds = {
          alinMassId: this.filters.massId ? [this.filters.massId] : massIds,
        };
        if (this.filters.status) finds.alinStatus = this.filters.status;
        if (this.filters.alruId) finds.alinAlruId = this.filters.alruId;

        const res = await AlertIncidentSvc.getIncidents({
          finds,
          orders: { alinStartedAt: 'desc' },
          params: { max: HISTORY_BATCH_SIZE },
        });

        if (res?.status?.errorCode) {
          throw new Error(res.status.message || 'Alert incident API returned an error');
        }

        if (seq !== this.fetchSeq) return;

        const allowed = new Set(massIds);
        const incidents = (res?.data || []).filter((i) => allowed.has(i.alinMassId));
        this.rawRows = buildRows(incidents);
      } catch (e) {
        if (seq !== this.fetchSeq) return;
        console.error('alert history fetch failed:', e);
        this.loadError = true;
        this.errorMessage = 'Failed to load alert history.';
        this.rawRows = [];
      } finally {
        if (seq === this.fetchSeq) this.isLoading = false;
      }
    },
  },
};
</script>
